import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Grid, LinearProgress, Typography } from '@mui/material';
import {
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    CheckCircle as CheckCircleIcon,
    Create as DrawIcon,
    Article as ArticleIcon,
    Assignment as AssignmentIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import mandataireSign from "src/Assets/Images/sign.png";
import mandatSign from "src/Assets/Images/sing-text.png";
import { PDFDocument } from 'pdf-lib';
import SignaturePad from 'react-signature-canvas';
import Teachr from 'src/Objects/models/Teachr';
import { prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import moment from 'moment';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import { useProfilContext } from 'src/Contexts/ProfilContexte';
import ButtonField from 'src/Components/Ui/ButtonField';
import FormInput from 'src/Components/Ui/FormInput';
import Settings from 'src/Components/Settings';
import ImageRepository from 'src/Repositories/ImageRepository';
import { ENV_DECLARONS, isBrandActive } from 'src/config/environment.config';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

const PDF_PATH = `${process.env.PUBLIC_URL}/${isBrandActive(ENV_DECLARONS) ? 'Pouvoir_creation_SAP_VDEF_2607_declarons.pdf' : `Pouvoir_creation_SAP_VDEF_2607.pdf`}`;

const MENTION_EXPECTED = 'bon pour pouvoir';

type SignStep = 'signature' | 'mention' | 'done';

const buildAddress = (addr: any): string =>
    [addr.numeroVoie, addr.typeVoie, addr.libelleVoie, addr.complement, addr.lieuDit, addr.codePostal, addr.ville]
        .filter(Boolean)
        .join(' ');

const buildSapFilledPdf = async (
    formData: any,
    options: { signatureDataURL?: string | null; withMention?: boolean; villeSignature?: string } = {}
): Promise<Blob> => {
    const { signatureDataURL = null, withMention = false, villeSignature = '' } = options;
    const res = await fetch(PDF_PATH);
    const arrayBuffer = await res.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const form = pdfDoc.getForm();

    const fill = (name: string, value: string) => {
        try { form.getTextField(name).setText(value); } catch { /* champ absent dans ce PDF */ }
    };
    const fillRadio = (name: string, value: string) => {
        try { form.getRadioGroup(name).select(value); } catch { /* champ absent dans ce PDF */ }
    };

    const address = buildAddress(formData.address ?? {});
    const civilite = formData.civilite ?? '';
    const fullName = `${formData.prenom ?? ''} ${formData.nom ?? ''}`.trim();
    const directorFullName = formData.director?.isSelf === false
        ? `${formData.director.prenom ?? ''} ${formData.director.nom ?? ''}`.trim()
        : fullName;
    const directorCivilite = formData.director?.isSelf === false
        ? (formData.director.civilite ?? civilite)
        : civilite;


    fill('text_1rgez', fullName);
    fill('text_2azmj', formData.dateNaissance ? moment(formData.dateNaissance).format('DD/MM/YYYY') : '');
    fill('text_3bvew', formData.villeNaissance ?? '');
    fill('text_16unmp', address);
    fill('text_4kpir', formData?.siret);

    fill('text_7cfjc', formData.codeApe ?? '');
    fill('text_8fxmo', formData.dateCreation ? moment(formData.dateCreation).format('DD/MM/YYYY') : '');
    fill('text_9psux', formData.telephone ?? '');
    fill('text_10xfe', formData.email ?? '');
    fill('text_6zzwq', moment().format('DD/MM/YYYY'));
    fill('text_5nlom', villeSignature);

    fillRadio('radio_group_1ghva', directorCivilite === "Mme" ? 'Value_njst' : 'Value_ymsg'); // Madame ou Monsieur
    // fill('dirigeant', directorFullName);
    fill('text_11jqek', formData.director?.isSelf === false ? (formData.director.nom ?? formData.nom ?? '') : (formData.nom ?? ''));
    fill('text_12nvnu', formData.director?.isSelf === false ? (formData.director.prenom ?? formData.prenom ?? '') : (formData.prenom ?? ''));
    fill('text_13jxjc', formData.director?.isSelf === false ? (formData.director.telephone ?? '') : (formData.telephone ?? ''));

    fill('bon_pour_pouvoir', withMention ? 'Bon pour pouvoir' : '');

    if (signatureDataURL) {
        try {
            const pages = pdfDoc.getPages();
            const currentPage = pages[0];
            const { width } = currentPage.getSize();

            const imageBytes1 = await fetch(signatureDataURL).then((res) => res.arrayBuffer());
            const imageBytes2 = await fetch(mandataireSign).then((res) => res.arrayBuffer());
            const imageBytes3 = await fetch(mandatSign).then((res) => res.arrayBuffer());

            const image1 = await pdfDoc.embedPng(imageBytes1);
            const image2 = await pdfDoc.embedPng(imageBytes2);
            const image3 = await pdfDoc.embedPng(imageBytes3);

            // Le bloc de signature n'est pas à la même hauteur selon le document :
            // sur la variante declarons il est plus haut dans la page (mise en
            // page différente), donc on remonte les images en conséquence.
            if (isBrandActive(ENV_DECLARONS)) {
                currentPage.drawImage(image1, { x: 120, y: 200, width: 100, height: 30 }); // signature du mandant
                currentPage.drawImage(image2, { x: 300, y: 195, width: 230, height: 40 }); // signature du mandataire
                currentPage.drawImage(image3, { x: 110, y: 228, width: 100, height: 20 }); // mention « Bon pour pouvoir »
            } else {
                currentPage.drawImage(image1, { x: 120, y: 90, width: 100, height: 30 });
                currentPage.drawImage(image2, { x: width - 90, y: 80, width: 230, height: 40 });
                currentPage.drawImage(image3, { x: 10, y: 100, width: 100, height: 20 });
            }
        } catch {
            /* position de signature non trouvée dans ce PDF */
        }
    }

    try { form.flatten(); } catch { /* certains PDFs résistent à l'aplatissement */ }

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
};

// ---- Data preview ----

interface DataPreviewProps {
    formData: any;
    primaryHex: string;
    fontFamily?: string;
}

const DataPreview: React.FC<DataPreviewProps> = ({ formData, primaryHex, fontFamily }) => {
    if (!formData) return null;
    const address = buildAddress(formData.address ?? {});

    const row = (label: string, value: string) => (
        <Box key={label} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ minWidth: 150, fontFamily }}>
                {label}
            </Typography>
            <Typography variant="caption" color="text.primary" fontWeight="500" sx={{ fontFamily, wordBreak: 'break-word' }}>
                {value || '-'}
            </Typography>
        </Box>
    );

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PersonIcon sx={{ color: '#0284c7', fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#0284c7', fontFamily }}>
                            Informations personnelles
                        </Typography>
                    </Box>
                    {row('Civilité', formData.civilite)}
                    {row('Nom', formData.nom)}
                    {row('Prénom', formData.prenom)}
                    {row('Date de naissance', formData.dateNaissance ? moment(formData.dateNaissance).format('DD/MM/YYYY') : '')}
                    {row('Ville de naissance', formData.villeNaissance)}
                    {row('Adresse', address)}
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <BusinessIcon sx={{ color: primaryHex, fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: primaryHex, fontFamily }}>
                            Informations de l'établissement
                        </Typography>
                    </Box>
                    {row('Code APE', formData.codeApe)}
                    {row('Date de création', formData.dateCreation ? moment(formData.dateCreation).format('DD/MM/YYYY') : '')}
                    {row('Téléphone', formData.telephone)}
                    {row('Email', formData.email)}
                    {formData.director?.isSelf === false && (
                        <>
                            {row('Dirigeant', `${formData.director.civilite ?? ''} ${formData.director.prenom ?? ''} ${formData.director.nom ?? ''}`.trim())}
                            {row('Tél. dirigeant', formData.director.telephone)}
                        </>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

// ---- Mention input ----

interface MentionInputProps {
    onValidated: () => void;
    primaryHex: string;
    fontFamily?: string;
}

const MentionInput: React.FC<MentionInputProps> = ({ onValidated, primaryHex, fontFamily }) => {
    const [value, setValue] = useState('');
    const [error, setError] = useState(false);
    const [validated, setValidated] = useState(false);

    const handleValidate = () => {
        const normalised = value.trim().toLowerCase().replace(/\s+/g, ' ');
        if (normalised === MENTION_EXPECTED) {
            setValidated(true);
            setError(false);
            onValidated();
        } else {
            setError(true);
            setTimeout(() => { setValue(''); setError(false); }, 2000);
        }
    };

    if (validated) {
        return (
            <Box sx={{ p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22, flexShrink: 0 }} />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="700" color="#166534" sx={{ fontFamily }}>
                            Mention validée
                        </Typography>
                        <Typography sx={{ fontFamily: "'Caveat', cursive", fontSize: '1.35rem', fontWeight: 600, color: '#1a3a1a', mt: 0.5 }}>
                            « Bon pour pouvoir »
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: error ? '#fca5a5' : 'grey.300', borderRadius: 2, transition: 'border-color 0.2s' }}>
            <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontFamily, mb: 0.5 }}>
                Mention obligatoire
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily, mb: 1.5 }}>
                Tapez exactement la mention « Bon pour pouvoir » pour confirmer votre pouvoir de création SAP.
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'white', border: '1px solid', borderColor: error ? '#fca5a5' : 'grey.300', borderRadius: 1.5, mb: 1.5 }}>
                <input
                    value={value}
                    onChange={e => { setValue(e.target.value); setError(false); }}
                    onKeyDown={e => e.key === 'Enter' && handleValidate()}
                    onPaste={e => e.preventDefault()}
                    type="text"
                    placeholder="Tapez : « Bon pour pouvoir »"
                    className="bg-transparent block w-full text-center text-base font-semibold border-b border-dashed border-gray-300 pb-2 focus:outline-none"
                    style={{ fontFamily, color: error ? '#dc2626' : '#111827' }}
                />
            </Box>
            {error && (
                <Typography variant="caption" color="error" sx={{ fontFamily, display: 'block', mb: 1, textAlign: 'center' }}>
                    Veuillez écrire exactement : « Bon pour pouvoir »
                </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ButtonField
                    label="Valider"
                    variant="contained"
                    className="font-bold"
                    onClick={handleValidate}
                    disabled={!value.trim()}
                />
            </Box>
        </Box>
    );
};

// ---- Main component ----

interface SapDocumentStepProps {
    onPrevious: () => void;
    onNext: () => void;
}

const SapDocumentStep: React.FC<SapDocumentStepProps> = ({ onPrevious, onNext }) => {
    const { teachr, dispatch } = useProfilContext();
    const { brandConfig } = useTailwindBrand();
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    const fontFamily = brandConfig.fontFamily;

    const formData = useSelector((state: any) => state.user?.sapFormData);

    const [numPages, setNumPages] = useState(0);
    const [pdfWidth, setPdfWidth] = useState(600);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const hasScrolledToEnd = scrollProgress >= 99.5;

    const [signStep, setSignStep] = useState<SignStep>('signature');
    const [villeSignature, setVilleSignature] = useState('');
    const [signatureDataURL, setSignatureDataURL] = useState<string | null>(null);
    const [showSignPad, setShowSignPad] = useState(true);
    const padRef = useRef<SignaturePad>(null);

    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    useEffect(() => {
        const update = () => {
            const container = scrollRef.current;
            if (container) setPdfWidth(Math.min(container.clientWidth - 16, 800));
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => {
            const max = el.scrollHeight - el.clientHeight;
            if (max <= 0) return;
            setScrollProgress(prev => Math.max(prev, Math.min((el.scrollTop / max) * 100, 100)));
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (!formData) return;
        let objectUrl: string | null = null;
        setIsLoadingPreview(true);
        buildSapFilledPdf(formData)
            .then(blob => {
                objectUrl = URL.createObjectURL(blob);
                setPreviewBlobUrl(objectUrl);
            })
            .catch(() => { /* fall back to raw PDF */ })
            .finally(() => setIsLoadingPreview(false));
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [formData]);

    const handleSignatureClear = () => {
        padRef.current?.clear();
        setSignatureDataURL(null);
        setShowSignPad(true);
    };

    const handleSignatureTrim = useCallback(() => {
        const url = padRef.current?.getTrimmedCanvas().toDataURL('image/png');
        if (url) setSignatureDataURL(url);
    }, []);

    const handleSignatureValidate = () => {
        if (!signatureDataURL) return;
        setShowSignPad(false);
        setSignStep('mention');
    };

    const handleSubmit = async () => {
        setIsGenerating(true);
        try {
            const blob = await buildSapFilledPdf(formData, { signatureDataURL, withMention: true, villeSignature });
            const url = URL.createObjectURL(blob);
            setDownloadUrl(url);

            const imageResponse = await ImageRepository.createImage(
                new File([blob], 'proof-is-sign-sap', { type: 'application/pdf' })
            );

            const updatedTeachr = prepareTeachrForUpdate({ ...teachr } as Teachr, { proofSignSap: imageResponse['@id'] });
            await updateTeachrData(updatedTeachr, dispatch);
            setSignStep('done');
            // const teachrResponse = await TeachrRepository.updateTeachr(updatedTeachr);
            // dispatch(userTeachrSetAccountAction(JSON.parse(JSON.stringify(teachrResponse))));
        } catch {
            /* erreur silencieuse - le bouton reste actif */
        } finally {
            setIsGenerating(false);
        }
    };

    const sectionTitle = (icon: React.ReactNode, title: string, subtitle?: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: `${primaryHex}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {React.cloneElement(icon as React.ReactElement, { sx: { color: primaryHex, fontSize: 20 } })}
            </Box>
            <Box>
                <Typography variant="subtitle1" fontWeight="700" fontFamily={fontFamily} color="text.primary">{title}</Typography>
                {subtitle && <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>{subtitle}</Typography>}
            </Box>
        </Box>
    );

    return (
        <Settings
            title="Pouvoir de création SAP"
            subtitle="Lisez attentivement le document puis signez pour finaliser votre procuration."
            icon={<AssignmentIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            {/* ── 1. Résumé des données ── */}
            <Grid item xs={12}>
                <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: `${primaryHex}30`, bgcolor: `${primaryHex}08` }}>
                    {sectionTitle(<ArticleIcon />, 'Informations pré-remplies dans le document', 'Ces données apparaîtront dans le pouvoir SAP')}
                    <DataPreview formData={formData} primaryHex={primaryHex} fontFamily={fontFamily} />
                </Box>
            </Grid>

            {/* ── 2. Lecteur PDF ── */}
            <Grid item xs={12}>
                <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                    {sectionTitle(<ArticleIcon />, 'Pouvoir de création SAP', "Faites défiler jusqu'à la dernière page pour débloquer la signature")}

                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>
                                Progression de lecture
                            </Typography>
                            <Typography variant="caption" fontWeight="700" sx={{ color: hasScrolledToEnd ? '#16a34a' : primaryHex, fontFamily }}>
                                {hasScrolledToEnd ? '✓ Lu entièrement' : `${Math.round(scrollProgress)}%`}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={scrollProgress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: hasScrolledToEnd ? '#16a34a' : primaryHex,
                                    borderRadius: 3,
                                },
                            }}
                        />
                    </Box>

                    <Box
                        ref={scrollRef}
                        sx={{
                            height: 520,
                            overflowY: 'auto',
                            bgcolor: '#525659',
                            borderRadius: 1.5,
                            p: 1,
                            scrollbarWidth: 'thin',
                            scrollbarColor: `${primaryHex} #ccc`,
                        }}
                    >
                        {isLoadingPreview && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 1.5 }}>
                                <CircularProgress sx={{ color: 'white' }} size={32} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    Chargement du document pré-rempli…
                                </Typography>
                            </Box>
                        )}
                        {!isLoadingPreview && (
                            <Document
                                file={previewBlobUrl ?? PDF_PATH}
                                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                                loading={
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                        <CircularProgress sx={{ color: 'white' }} />
                                    </Box>
                                }
                            >
                                {Array.from({ length: numPages }, (_, i) => (
                                    <Box key={i} sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Page pageNumber={i + 1} width={pdfWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                                    </Box>
                                ))}
                            </Document>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: hasScrolledToEnd ? '#f0fdf4' : '#fffbeb', border: '1px solid', borderColor: hasScrolledToEnd ? '#bbf7d0' : '#fde68a' }}>
                        {hasScrolledToEnd
                            ? <LockOpenIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                            : <LockIcon sx={{ color: '#d97706', fontSize: 18 }} />
                        }
                        <Typography variant="caption" fontWeight="600" sx={{ color: hasScrolledToEnd ? '#166534' : '#92400e', fontFamily }}>
                            {hasScrolledToEnd
                                ? 'Document lu entièrement, vous pouvez maintenant signer.'
                                : "Faites défiler jusqu'à la fin du document pour débloquer la signature."}
                        </Typography>
                    </Box>
                </Box>
            </Grid>

            {/* ── 3. Signature & mention (verrouillé jusqu'à fin de lecture) ── */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        bgcolor: hasScrolledToEnd ? 'background.paper' : 'grey.100',
                        opacity: hasScrolledToEnd ? 1 : 0.45,
                        pointerEvents: hasScrolledToEnd ? 'auto' : 'none',
                        transition: 'opacity 0.4s',
                        position: 'relative',
                    }}
                >
                    {!hasScrolledToEnd && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <LockIcon sx={{ color: '#d97706', fontSize: 36 }} />
                                <Typography variant="body2" fontWeight="700" color="#92400e" fontFamily={fontFamily} textAlign="center">
                                    Lisez le document en entier pour débloquer
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {sectionTitle(<DrawIcon />, 'Signature & mention obligatoire', 'Complétez les deux étapes pour finaliser votre procuration')}

                    {/* Indicateurs d'étapes */}
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                        {[
                            { key: 'signature', label: '1. Signature' },
                            { key: 'mention', label: '2. Bon pour pouvoir' },
                        ].map(({ key, label }) => {
                            const order: SignStep[] = ['signature', 'mention', 'done'];
                            const current = order.indexOf(signStep);
                            const thisIdx = order.indexOf(key as SignStep);
                            const isDone = current > thisIdx || signStep === 'done';
                            const isActive = key === signStep;
                            return (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, border: '1px solid', borderColor: isDone ? '#bbf7d0' : isActive ? primaryHex : 'grey.300', bgcolor: isDone ? '#f0fdf4' : isActive ? `${primaryHex}12` : 'transparent' }}>
                                    {isDone
                                        ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 14 }} />
                                        : <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: isActive ? primaryHex : 'grey.300' }} />
                                    }
                                    <Typography variant="caption" fontWeight="600" sx={{ color: isDone ? '#166534' : isActive ? primaryHex : 'text.secondary', fontFamily }}>
                                        {label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Étape 1 : Signature */}
                        {signStep === 'signature' && (
                            <Box sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontFamily, mb: 0.5 }}>
                                    Votre signature
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily, mb: 1.5 }}>
                                    Signez dans le cadre ci-dessous. Vérifiez que votre signature est lisible avant de valider.
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                    <FormInput
                                        label="Fait à (ville)"
                                        value={villeSignature}
                                        onChange={e => setVilleSignature(e.target.value)}
                                        required
                                    />
                                </Box>
                                {showSignPad ? (
                                    <Box sx={{ bgcolor: 'white', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.300', p: 1, position: 'relative' }}>
                                        {!signatureDataURL && (
                                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', textAlign: 'center' }}>
                                                <Typography variant="body2" color="grey.400" fontWeight="600" fontFamily={fontFamily}>
                                                    Signer ici
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                            <button onClick={handleSignatureClear} className="text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors">
                                                Effacer
                                            </button>
                                        </Box>
                                        <SignaturePad
                                            ref={padRef}
                                            onEnd={handleSignatureTrim}
                                            onBegin={() => setSignatureDataURL(null)}
                                            velocityFilterWeight={0.2}
                                            maxWidth={1.5}
                                            minWidth={1}
                                            canvasProps={{ className: 'w-full', style: { height: 160, cursor: 'crosshair' } }}
                                        />
                                    </Box>
                                ) : (
                                    signatureDataURL && (
                                        <Box sx={{ bgcolor: 'white', borderRadius: 1.5, border: '1px solid #bbf7d0', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <img src={signatureDataURL} alt="Signature" style={{ maxHeight: 64, maxWidth: '60%', objectFit: 'contain' }} />
                                            <ButtonField label="Modifier" variant="outlined" className="font-bold" onClick={handleSignatureClear} />
                                        </Box>
                                    )
                                )}
                                {showSignPad && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                                        <ButtonField
                                            label="Valider la signature"
                                            variant="contained"
                                            className="font-bold"
                                            onClick={handleSignatureValidate}
                                            disabled={!signatureDataURL || !villeSignature.trim()}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Résumé signature validée */}
                        {signStep !== 'signature' && signatureDataURL && (
                            <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                <img src={signatureDataURL} alt="Signature validée" style={{ maxHeight: 40, objectFit: 'contain' }} />
                                <Typography variant="caption" fontWeight="700" color="#166534" fontFamily={fontFamily}>Signature validée</Typography>
                            </Box>
                        )}

                        {/* Étape 2 : Mention */}
                        {signStep !== 'signature' && (
                            <MentionInput
                                onValidated={() => setSignStep('done')}
                                primaryHex={primaryHex}
                                fontFamily={fontFamily}
                            />
                        )}

                        {/* Étape terminée */}
                        {signStep === 'done' && (
                            <Box sx={{ p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="700" color="#166534" fontFamily={fontFamily}>
                                            Toutes les étapes sont complétées !
                                        </Typography>
                                        <Typography variant="body2" color="#166534" fontFamily={fontFamily}>
                                            Vous pouvez télécharger votre exemplaire signé, puis confirmer votre procuration.
                                        </Typography>
                                    </Box>
                                </Box>
                                {downloadUrl ? (
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <a href={downloadUrl} download="pouvoir-sap-signe.pdf" style={{ textDecoration: 'none' }}>
                                            <ButtonField
                                                label="Télécharger mon exemplaire"
                                                variant="outlined"
                                                className="font-bold"
                                                startIcon={<DownloadIcon />}
                                            />
                                        </a>
                                        <ButtonField
                                            label="Finaliser ma procuration →"
                                            variant="contained"
                                            className="font-bold"
                                            onClick={onNext}
                                        />
                                    </Box>
                                ) : (
                                    <ButtonField
                                        label={isGenerating ? 'Génération en cours...' : 'Générer le document signé'}
                                        variant="contained"
                                        className="font-bold"
                                        onClick={handleSubmit}
                                        disabled={isGenerating}
                                        startIcon={isGenerating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>

            {/* Navigation */}
            {/* <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', pt: 1 }}>
                    <ButtonField
                        label="Précédent"
                        variant="outlined"
                        className="font-bold"
                        onClick={onPrevious}
                    />
                    <ButtonField
                        label="Finaliser ma procuration →"
                        variant="contained"
                        className="font-bold"
                        onClick={onNext}
                    />
                </Box>
            </Grid> */}
        </Settings>
    );
};

export default SapDocumentStep;
